import { useCallback, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter, usePathname } from '@/i18n/navigation'
import { saveBranchData } from '../_store/common/common.slice'
import { IRedux } from '../_store/types'
import { IBranch } from '../_types'
import { useActiveBrand } from './useActiveBrand'

interface IData {
  id: IBranch['id']
  menu_id: IBranch['menu_id']
  address: IBranch['address']
  name: IBranch['name']
  location: IBranch['location']
  slug: IBranch['slug']
  future_delivery_order_time: IBranch['future_delivery_order_time']
  future_self_pickup_order_time: IBranch['future_self_pickup_order_time']
  work_hour_start: IBranch['work_hour_start']
  work_hour_end: IBranch['work_hour_end']
  is_work_24_7?: IBranch['is_work_24_7']
  delivery_time: IBranch['delivery_time']
  shipper_id: string
}

/**
 * Custom hook that wraps saveBranchData and automatically refreshes
 * the router when menu_id changes, but ONLY on the home page.
 * This ensures server components refetch data without affecting UX on other pages.
 *
 * Uses a ref to track the current menu_id to prevent infinite refresh loops
 * while keeping the callback stable for use in dependency arrays.
 */
export function useSaveBranchData() {
  const dispatch = useDispatch()
  const router = useRouter()
  const pathname = usePathname()
  const shipperId = useActiveBrand()

  // Track current menu_id in Redux state
  const currentMenuId = useSelector(
    (state: IRedux) => state.common.branches?.[shipperId]?.menu_id
  )
  // Use ref to track menu_id without causing callback recreation
  const menuIdRef = useRef<string | undefined>(currentMenuId)

  // Update ref when Redux state changes
  useEffect(() => {
    menuIdRef.current = currentMenuId
  }, [currentMenuId])

  // Check if we're on the home page (root path)
  // Home page pathname is '/' or '/{locale}' (e.g., '/en', '/ru', '/uz', '/kk')
  // We check if pathname has at most 2 segments (empty string + locale, or just '/')
  const pathSegments = pathname.split('/').filter(Boolean)
  const isHomePage =
    pathname === '/' ||
    pathSegments.length === 1

  // Stable callback that doesn't recreate when menu_id changes
  const saveBranch = useCallback(
    (branchData: IData) => {
      const previousMenuId = menuIdRef.current
      const newMenuId = branchData?.menu_id

      // Dispatch the action to update Redux state and cookies
      dispatch(saveBranchData(branchData))

      // Update ref immediately for next comparison
      menuIdRef.current = newMenuId

      // Only refresh on home page to avoid unnecessary re-renders on checkout/product pages
      // This prevents UX issues like flickering, unnecessary loading states, etc.
      if (previousMenuId !== newMenuId && isHomePage) {
        router.refresh()
      }
    },
    [dispatch, router, isHomePage]
  )

  return saveBranch
}
